import type { OmegaNodeForEach, OmegaNodeSubGraph, WorkflowEdge, WorkflowNode } from "@/types/workflows";
import { isContainerOmegaNode } from "@/types/workflows";
import { getId } from "../../../../lib/generate-id";
import type { OmegaGraph } from "./workflow-chat-types";

function makeEdge(from: string, to: string): WorkflowEdge {
  return { id: `edge_${from}_${to}`, from, to };
}

function outgoingEdges(graph: OmegaGraph, nodeId: string): WorkflowEdge[] {
  return graph.edges.filter((edge) => edge.from === nodeId);
}

function makeInnerGraph(): { nodes: WorkflowNode[]; edges: WorkflowEdge[] } {
  const startId = getId("sub_graph_start");
  const endId = getId("sub_graph_end");
  return {
    nodes: [
      {
        type: "sub_graph_start",
        id: startId,
        name: "Entry",
        displayInfo: { position: { x: 0, y: 0 } },
      } as WorkflowNode,
      {
        type: "sub_graph_end",
        id: endId,
        name: "Exit",
        displayInfo: { position: { x: 0, y: 0 } },
      } as WorkflowNode,
    ],
    edges: [makeEdge(startId, endId)],
  };
}

export function makeSubGraphNode(
  name: string,
  description: string,
  id?: string
): OmegaNodeSubGraph {
  return {
    type: "sub_graph",
    id: id ?? getId("sub_graph"),
    name,
    description,
    displayInfo: { position: { x: 0, y: 0 } },
    graph: makeInnerGraph(),
  };
}

export function makeForEachNode(
  name: string,
  description: string,
  id?: string
): OmegaNodeForEach {
  return {
    type: "foreach",
    id: id ?? getId("foreach"),
    name,
    description,
    parameters: {
      collection: { type: "cel", value: "" },
    },
    displayInfo: { position: { x: 0, y: 0 } },
    graph: makeInnerGraph(),
  };
}

export function getSubGraphDescription(params?: Record<string, unknown>): string {
  return typeof params?.description === "string" ? params.description : "";
}

/**
 * Find which graph (outer or a SubGraph's inner graph) contains a given node.
 * Returns { graph, path } where path is the list of SubGraph node IDs to
 * traverse to reach the graph, or an empty array for the outer graph.
 */
export function findContainingGraph(
  graph: OmegaGraph,
  nodeId: string
): { graph: OmegaGraph; path: string[] } | undefined {
  if (graph.nodes.some((node) => node.id === nodeId)) {
    return { graph, path: [] };
  }

  for (const node of graph.nodes) {
    if (isContainerOmegaNode(node)) {
      const inner = findContainingGraph(node.graph, nodeId);
      if (inner) {
        return { graph: inner.graph, path: [node.id, ...inner.path] };
      }
    }
  }

  return undefined;
}

/**
 * Replace a SubGraph's inner graph within the outer graph.
 * Traverses the path of SubGraph IDs to reach the target graph.
 */
export function replaceInnerGraph(
  outerGraph: OmegaGraph,
  path: string[],
  newInnerGraph: OmegaGraph
): OmegaGraph {
  if (path.length === 0) return newInnerGraph;

  const [subGraphId, ...restPath] = path;
  return {
    ...outerGraph,
    nodes: outerGraph.nodes.map((node) => {
      if (node.id === subGraphId && isContainerOmegaNode(node)) {
        return {
          ...node,
          graph: replaceInnerGraph(node.graph, restPath, newInnerGraph),
        } as WorkflowNode;
      }
      return node;
    }),
    edges: outerGraph.edges,
  };
}

/**
 * Add a SubGraph node after an existing node.
 * Creates the SubGraph with inner start/end nodes pre-populated.
 */
export function addSubGraph(
  graph: OmegaGraph,
  afterNodeId: string,
  name: string = "Sub Graph",
  description: string = "",
  id?: string
): OmegaGraph {
  const afterNode = graph.nodes.find((node) => node.id === afterNodeId);
  if (!afterNode) {
    throw new Error(`Node not found: ${afterNodeId}`);
  }

  const subGraphNode = makeSubGraphNode(name, description, id);
  const outEdges = outgoingEdges(graph, afterNodeId);

  let nodes: WorkflowNode[] = [...graph.nodes, subGraphNode];
  let edges = [...graph.edges];

  if (outEdges.length >= 1) {
    const oldEdge = outEdges[0];
    edges = [
      ...edges.filter((edge) => edge.id !== oldEdge.id),
      makeEdge(afterNodeId, subGraphNode.id),
      makeEdge(subGraphNode.id, oldEdge.to),
    ];
  } else {
    edges.push(makeEdge(afterNodeId, subGraphNode.id));
  }

  return { nodes, edges };
}

/**
 * Insert a SubGraph between two connected nodes by splitting the edge.
 */
export function insertSubGraphBetween(
  graph: OmegaGraph,
  sourceId: string,
  targetId: string,
  name: string = "Sub Graph",
  description: string = "",
  id?: string
): OmegaGraph {
  const edge = graph.edges.find(
    (currentEdge) => currentEdge.from === sourceId && currentEdge.to === targetId
  );
  if (!edge) {
    throw new Error(`No edge found from ${sourceId} to ${targetId}`);
  }

  const subGraphNode = makeSubGraphNode(name, description, id);

  return {
    nodes: [...graph.nodes, subGraphNode],
    edges: [
      ...graph.edges.filter((currentEdge) => currentEdge.id !== edge.id),
      makeEdge(sourceId, subGraphNode.id),
      makeEdge(subGraphNode.id, targetId),
    ],
  };
}

export function addForEach(
  graph: OmegaGraph,
  afterNodeId: string,
  name: string = "For Each",
  description: string = "",
  id?: string
): OmegaGraph {
  const afterNode = graph.nodes.find((node) => node.id === afterNodeId);
  if (!afterNode) {
    throw new Error(`Node not found: ${afterNodeId}`);
  }

  const forEachNode = makeForEachNode(name, description, id);
  const outEdges = outgoingEdges(graph, afterNodeId);

  let nodes: WorkflowNode[] = [...graph.nodes, forEachNode];
  let edges = [...graph.edges];

  if (outEdges.length >= 1) {
    const oldEdge = outEdges[0];
    edges = [
      ...edges.filter((edge) => edge.id !== oldEdge.id),
      makeEdge(afterNodeId, forEachNode.id),
      makeEdge(forEachNode.id, oldEdge.to),
    ];
  } else {
    edges.push(makeEdge(afterNodeId, forEachNode.id));
  }

  return { nodes, edges };
}

export function insertForEachBetween(
  graph: OmegaGraph,
  sourceId: string,
  targetId: string,
  name: string = "For Each",
  description: string = "",
  id?: string
): OmegaGraph {
  const edge = graph.edges.find(
    (currentEdge) => currentEdge.from === sourceId && currentEdge.to === targetId
  );
  if (!edge) {
    throw new Error(`No edge found from ${sourceId} to ${targetId}`);
  }

  const forEachNode = makeForEachNode(name, description, id);

  return {
    nodes: [...graph.nodes, forEachNode],
    edges: [
      ...graph.edges.filter((currentEdge) => currentEdge.id !== edge.id),
      makeEdge(sourceId, forEachNode.id),
      makeEdge(forEachNode.id, targetId),
    ],
  };
}
